import React, { useState } from "react";
import { ResolutionKeys, UserDefinedAttribute, UserDefinedDataset } from "../types";
import CloseIcon from '@mui/icons-material/Close';
import { Select, MenuItem, FormControl } from '@mui/material';
import ReactDOM from 'react-dom'; // Import ReactDOM for portal

interface ImportButtonProps {
  setAvailableUserDatasets: React.Dispatch<React.SetStateAction<UserDefinedDataset>>
  setCurrentUserDataset: React.Dispatch<React.SetStateAction<string>>;
  setAvailableResolutions: React.Dispatch<React.SetStateAction<ResolutionKeys[]>>;
  setShowUserDataset: (user_data: UserDefinedDataset, current_user_data: string) => void;
  setAvailableDatasets: React.Dispatch<React.SetStateAction<{ categories: string[]; names: string[]; titles: string[]; }>>;
  current_datalist: { names: string[]; titles: string[]; categories: string[]},
  is_overlay? : string;
}

interface ColorScheme {
  name: string;
  type: string;
  gradient?: string;
  colors?: string[];
  isDiscrete?: boolean;
}

const ImportButton: React.FC<ImportButtonProps> = ({ 
  setAvailableUserDatasets, 
  setCurrentUserDataset, 
  setShowUserDataset, 
  setAvailableResolutions,
  setAvailableDatasets,
  current_datalist,
  is_overlay = "False"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [selectedColorScheme, setSelectedColorScheme] = useState<string>("sequential_greys");
  const [resolution, setResolution] = useState<ResolutionKeys>("state");
  const [file, setFile] = useState<File | null>(null);

  const colorSchemes: { [key: string]: ColorScheme } = {
    sequential_greys: { 
      name: "Sequential - Greys", 
      type: "sequential",
      gradient: 'linear-gradient(to right, #ffffff, #808080, #000000)'
    },
    diverging_rdylbu: { 
      name: "Diverging - RdYlBu", 
      type: "diverging",
      gradient: 'linear-gradient(to right, #d73027, #fc8d59, #fee090, #91bfdb, #4575b4)'
    },
    qualitative_tab10: { 
      name: "Qualitative - Tab10",
      type: "qualitative",
      colors: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'],
      isDiscrete: true
    }
  };

  const parseCSV = (csvText: string) => {
    const lines = csvText.split("\n").map(line => line.trim()).filter(line => line);
    const result: { [key: string]:  string } = {};
    
    lines.slice(1).forEach(line => { // Skip header
      const [key, , value] = line.split(",");
      if (key && value) {
        result[key.trim()] = value.trim();
      }
    });

    const values = Object.values(result);

    var current_user_dataset : UserDefinedAttribute = {
      polygon_values: result,
      min: undefined,
      max: undefined,
      resolution: resolution,
      categories: {},
      polygon_type: "Continuous",
      colormap_bases: selectedColorScheme.split("_")[1].toLowerCase(),
      colormap_types: selectedColorScheme.split("_")[0].toLowerCase(),
    }

    if (values.every(x => !isNaN(Number(x)))){
      current_user_dataset["min"] = Math.min(...values.map((x) => Number(x)));
      current_user_dataset["max"] = Math.max(...values.map((x) => Number(x)));
      current_user_dataset.polygon_type = "Continuous"
    }
    else {
      current_user_dataset.categories = values.reduce((acc, key, _, array) => {
        if (!(key in acc)) {
          // Find the next available number (1-based)
          const nextNumber = Object.keys(acc).length + 1;
          acc[key] = nextNumber;
        }
        return acc;
      }, {} as { [key: string]: number });

       current_user_dataset.polygon_type = "Discrete"
    }

    return current_user_dataset;
  }

  const handleClose = () => {
    setIsOpen(false);
    setName("");
    setSelectedColorScheme("sequential_greys");
    setResolution("state");
    setFile(null);
  };

  const handleSave = () => {
    if(!file) return null;
    if(!name) return null;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      if (!e.target?.result) return;
      const text = e.target.result as string;
      
      const current_dataset = parseCSV(text);

      setAvailableUserDatasets((prevDatasets) => {
        const updatedDatasets: UserDefinedDataset = {
          ...prevDatasets,
          [name]: current_dataset,
        };

        const updatedDatalist = {
          names: [...current_datalist.names, name],
          titles: [...current_datalist.titles, name],
          categories: [...current_datalist.categories, "Custom Datasets"]
        };
        setAvailableDatasets(updatedDatalist);

        // Store the updated dataset in localStorage
        localStorage.setItem("user_datasets", JSON.stringify(updatedDatasets));

        //set dropdown selector
        setCurrentUserDataset(name);

        //update states!!!!
        setShowUserDataset(updatedDatasets, name);

        //update resolutions
        setAvailableResolutions([]);

        return updatedDatasets;
      });

    };
    reader.readAsText(file);
    
    handleClose();
  };

  // Create a portal to render the modal at the document body level
  const renderModal = () => {
    if (!isOpen) return null;
    
    return ReactDOM.createPortal(
      <div className="modal-overlay">
        <div className="modal-content">
          <button className="close-button" onClick={handleClose}>
            <CloseIcon style={{ color: '#4a5568' }} />
          </button>

          <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#1a365d' }}>Import Data</h2>

          <div className="form-group">
            <label className="form-label">Dataset Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="form-input"
              style={{ padding: '8px', width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Color Scheme</label>
            <Select
              value={selectedColorScheme}
              onChange={(e) => setSelectedColorScheme(e.target.value as string)}
              fullWidth
              MenuProps={{
                container: document.body,
                style: { zIndex: 10002 },
                disablePortal: false,
                disableScrollLock: false
              }}
              renderValue={(selected) => {
                const scheme = colorSchemes[selected as string];
                return (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    width: '100%',
                    height: '100%'
                  }}>
                    <span style={{ marginRight: '10px' }}>{scheme?.name}</span>
                    {scheme?.isDiscrete && scheme?.colors ? (
                      <div style={{ 
                        display: 'flex', 
                        flexGrow: 1,
                        height: '20px',
                        maxWidth: 'calc(100% - 60px)',
                        marginRight: '24px',
                        border: '1px solid #ddd',
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        {scheme.colors.map((color, index) => (
                          <div 
                            key={index}
                            style={{
                              flex: 1,
                              backgroundColor: color,
                              height: '100%'
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div style={{ 
                        flexGrow: 1,
                        height: '20px',
                        maxWidth: 'calc(100% - 60px)',
                        marginRight: '24px',
                        background: scheme?.gradient,
                        borderRadius: '2px',
                        border: '1px solid #ddd'
                      }} />
                    )}
                  </div>
                );
              }}
              sx={{
                height: '38px',
                '.MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 14px',
                }
              }}
            >
              {Object.entries(colorSchemes).map(([key, scheme]) => (
                <MenuItem key={key} value={key}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                    <span>{scheme.name}</span>
                    {scheme.isDiscrete && scheme.colors ? (
                      <div style={{ 
                        display: 'flex', 
                        width: '120px',
                        height: '20px',
                        marginLeft: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        {scheme.colors.map((color, index) => (
                          <div 
                            key={index}
                            style={{
                              flex: 1,
                              backgroundColor: color,
                              height: '100%'
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div style={{ 
                        width: '120px',
                        height: '20px',
                        background: scheme.gradient,
                        borderRadius: '2px',
                        border: '1px solid #ddd',
                        marginLeft: '8px'
                      }} />
                    )}
                  </div>
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className="form-group">
            <label className="form-label">Resolution</label>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '8px', 
              marginTop: '8px' 
            }}>
              {['State', 'County', 'HUC2', 'HUC4', 'HUC6', 'HUC8'].map((res) => (
                <button
                  key={res}
                  onClick={() => setResolution(res.toLowerCase() as ResolutionKeys)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: resolution === res.toLowerCase() ? '#1976d2' : 'white',
                    color: resolution === res.toLowerCase() ? 'white' : '#333',
                    border: `1px solid ${resolution === res.toLowerCase() ? '#1976d2' : '#e2e8f0'}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: resolution === res.toLowerCase() ? '600' : 'normal',
                    minWidth: '70px',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {res}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Upload CSV</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="file-input"
            />
          </div>

          <div className="modal-footer">
            <button 
              onClick={handleClose}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#4a5568',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Upload Dataset
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <button disabled={is_overlay == "True" ? true : false} style={{margin: "0px"}} className="export-map-button" onClick={() => setIsOpen(true)}>
        Import Data
      </button>

      {renderModal()}
    </>
  );
};

export default ImportButton;